import {
  Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { ApiError, api } from "../lib/api";
import { useTheme } from "../lib/theme";
import { Icon } from "./Icon";
import { SandboxTerminal, TerminalUnavailable } from "./SandboxTerminal";

/**
 * The sandbox, docked at the bottom of the module pages so a learner can try each command, and
 * edit files, while they read. Two tabs: the terminal and a text editor. It stays open as they
 * move between lessons (the shell is a tmux session in their container, so even a page reload
 * lands back in it), and it is the same shell and the same files as the lab. The lab page has
 * its own terminal and editor, so the dock is not offered there.
 */

export type DockMode = "terminal" | "editor";

interface Dock {
  /** Whether the dock is offered on this page at all. */
  available: boolean;
  open: (mode?: DockMode) => void;
}

const DockContext = createContext<Dock>({ available: false, open: () => undefined });

export function useSandboxDock(): Dock {
  return useContext(DockContext);
}

/** CodeMirror is only for learners who open the editor. */
const DockEditor = lazy(() => import("./DockEditor"));

/** Per-viewer convenience only: whether the dock was left open, how tall, and on which tab. */
const STORAGE_KEY = "tfl.sandboxDock";

function remembered(): { open: boolean; tall: boolean; mode: DockMode } {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
    return { open: raw.open === true, tall: raw.tall === true, mode: raw.mode === "editor" ? "editor" : "terminal" };
  } catch {
    return { open: false, tall: false, mode: "terminal" };
  }
}

type Phase = "idle" | "starting" | "ready" | "failed";

export function SandboxDockProvider({ available, children }: { available: boolean; children: ReactNode }) {
  const { resolved } = useTheme();
  const [open, setOpen] = useState(() => remembered().open);
  const [tall, setTall] = useState(() => remembered().tall);
  const [mode, setMode] = useState<DockMode>(() => remembered().mode);
  // The editor is mounted the first time its tab is shown, then kept, so its open file survives
  // a trip to the terminal and back.
  const [editorUsed, setEditorUsed] = useState(() => remembered().open && remembered().mode === "editor");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ open, tall, mode }));
    } catch { /* private mode: the dock just forgets */ }
  }, [open, tall, mode]);

  const start = useCallback(async () => {
    setPhase("starting");
    setError(null);
    try {
      // Starting a running sandbox is a no-op; a stopped one boots and restores the learner's files.
      await api.sandboxStart();
      setPhase("ready");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Your sandbox could not be started.");
      setPhase("failed");
    }
  }, []);

  // Opening the dock is what starts the sandbox, never merely loading a lesson.
  useEffect(() => {
    if (available && open && phase === "idle") void start();
  }, [available, open, phase, start]);

  const show = useCallback((next: DockMode) => {
    setMode(next);
    if (next === "editor") setEditorUsed(true);
    setOpen(true);
  }, []);

  const value = useMemo<Dock>(() => ({
    available, open: (next = "terminal") => show(next),
  }), [available, show]);
  const shown = available && open;

  const notReady = (
    <TerminalUnavailable
      title={phase === "failed" ? "Your sandbox did not start" : "Starting your sandbox"}
      action={phase === "failed" ? (
        <button type="button" className="btn btn-sm" onClick={() => void start()}>
          Try again
        </button>
      ) : <span className="spinner" />}
    >
      {phase === "failed" ? error : "The first start takes up to a minute; after that it is quick."}
    </TerminalUnavailable>
  );

  return (
    <DockContext.Provider value={value}>
      <div className={shown ? (tall ? "dock-space is-open is-tall" : "dock-space is-open") : "dock-space"}>
        {children}
      </div>

      {available && !open ? (
        <div className="sandbox-dock-toggles">
          <button type="button" className="btn sandbox-dock-toggle" onClick={() => show("editor")}>
            <Icon name="file" size={16} />
            Editor
          </button>
          <button type="button" className="btn btn-primary sandbox-dock-toggle" onClick={() => show("terminal")}>
            <Icon name="terminal" size={16} />
            Sandbox
          </button>
        </div>
      ) : null}

      {shown ? (
        <section className={tall ? "sandbox-dock is-tall" : "sandbox-dock"} aria-label="Your sandbox">
          <div className="sandbox-dock-bar">
            <div className="dock-tabs" role="tablist" aria-label="Sandbox tools">
              <button
                type="button"
                role="tab"
                aria-selected={mode === "terminal"}
                className={mode === "terminal" ? "dock-tab is-current" : "dock-tab"}
                onClick={() => show("terminal")}
              >
                <Icon name="terminal" size={13} />
                Terminal
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "editor"}
                className={mode === "editor" ? "dock-tab is-current" : "dock-tab"}
                onClick={() => show("editor")}
              >
                <Icon name="file" size={13} />
                Editor
              </button>
            </div>
            <span className="dim">Same files and shell as your labs, saved between visits.</span>
            <span className="spread row">
              <button
                type="button"
                className="btn btn-sm btn-quiet"
                onClick={() => setTall((t) => !t)}
                aria-pressed={tall}
              >
                <Icon name={tall ? "chevronDown" : "chevronRight"} size={13} />
                {tall ? "Smaller" : "Taller"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-quiet"
                onClick={() => setOpen(false)}
                aria-label="Hide your sandbox"
              >
                <Icon name="cross" size={13} />
                Hide
              </button>
            </span>
          </div>

          <div className="pane terminal-pane sandbox-dock-pane" role="tabpanel" hidden={mode !== "terminal"}>
            {phase === "ready" ? (
              <SandboxTerminal session="main" dark={resolved === "dark"} active />
            ) : notReady}
          </div>

          {editorUsed ? (
            <div className="pane sandbox-dock-pane" role="tabpanel" hidden={mode !== "editor"}>
              {phase === "ready" ? (
                <Suspense fallback={<div className="dock-editor-empty"><span className="spinner" /></div>}>
                  <DockEditor dark={resolved === "dark"} />
                </Suspense>
              ) : <div className="terminal-pane">{notReady}</div>}
            </div>
          ) : null}
        </section>
      ) : null}
    </DockContext.Provider>
  );
}
